import { BookingsLineItemOption } from '@wix/bookings-checkout-api';
import { ServiceType } from '@wix/bookings-uou-types';
import { FormNestedSlot } from '../../types/formSelectedSlots';
import { convertToTimeZone, hasTimezoneSuffix } from '../time';
import type { Experiments } from '@wix/yoshi-flow-editor';
import { ExperimentsConsts } from '../../consts/experiments';

type TimeSegment = {
  startDate: Date;
  endDate: Date;
  resourceName?: string | null;
};

const mapSlotToTimeSegment = (
  slot: {
    startDate?: string | null;
    endDate?: string | null;
    resource?: { id?: string; name?: string | null };
    timezone?: string | null;
  },
  shouldConvertSlotToTimeZone: boolean,
): TimeSegment => {
  function convertToTimeZoneIfNeeded(date: string, timezone: string): Date {
    const shouldConvertToTimeZone =
      shouldConvertSlotToTimeZone && !hasTimezoneSuffix(date);
    return shouldConvertToTimeZone
      ? convertToTimeZone(date, timezone)
      : new Date(date);
  }

  return {
    startDate: convertToTimeZoneIfNeeded(slot.startDate!, slot.timezone!),
    endDate: convertToTimeZoneIfNeeded(slot.endDate!, slot.timezone!),
    ...(slot.resource ? { resourceName: slot.resource?.name } : {}),
  };
};

export const findOverlappingResource = (
  currentSlots: FormNestedSlot[],
  bookingsLineItemOptions: BookingsLineItemOption[],
  experiments: Experiments,
): string | null | undefined => {
  const shouldConvertSlotsToTimeZone = experiments.enabled(
    ExperimentsConsts.CartConflictEnableSlotsTimezoneConversion,
  );
  const slotResources = currentSlots.map((slot) => slot.resource?.id);
  const sameStaffBookingsTimeSegments = bookingsLineItemOptions
    .filter(
      (itemOption) =>
        itemOption.slot?.resource?.id &&
        slotResources.includes(itemOption.slot.resource.id) &&
        itemOption.tags?.includes(ServiceType.INDIVIDUAL),
    )
    .map((itemOption) =>
      mapSlotToTimeSegment(itemOption.slot!, shouldConvertSlotsToTimeZone),
    )
    .concat(
      currentSlots.map((slot) =>
        mapSlotToTimeSegment(slot, shouldConvertSlotsToTimeZone),
      ),
    );

  sameStaffBookingsTimeSegments.sort(
    (a: TimeSegment, b: TimeSegment) =>
      a.startDate.getTime() - b.startDate.getTime(),
  );

  for (let i = 0; i < sameStaffBookingsTimeSegments.length - 1; i++) {
    if (
      sameStaffBookingsTimeSegments[i].endDate.getTime() >
      sameStaffBookingsTimeSegments[i + 1].startDate.getTime()
    ) {
      return sameStaffBookingsTimeSegments[i].resourceName;
    }
  }
};
